import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, 
  Box, Typography, Alert, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation?: string;
}

interface CSVImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (questions: QuizQuestion[]) => void;
}

const CSVImportDialog: React.FC<CSVImportDialogProps> = ({ open, onClose, onImport }) => {
  const [csvData, setCsvData] = useState<QuizQuestion[]>([]);
  const [error, setError] = useState<string>('');
  const [previewData, setPreviewData] = useState<any[]>([]);

  const generateId = () => `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setError('CSV file must have at least a header row and one data row');
          return;
        }

        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        const requiredHeaders = ['question', 'option1', 'option2', 'option3', 'option4', 'correct_answer'];
        
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          setError(`Missing required headers: ${missingHeaders.join(', ')}`);
          return;
        }

        const questions: QuizQuestion[] = [];
        const preview: any[] = [];

        for (let i = 1; i < Math.min(lines.length, 6); i++) { // Preview first 5 rows
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 6) {
            const question: QuizQuestion = {
              id: generateId(),
              question: values[headers.indexOf('question')],
              options: [
                values[headers.indexOf('option1')],
                values[headers.indexOf('option2')],
                values[headers.indexOf('option3')],
                values[headers.indexOf('option4')]
              ],
              correct_answer: parseInt(values[headers.indexOf('correct_answer')]) - 1, // Convert to 0-based index
              explanation: values[headers.indexOf('explanation')] || ''
            };
            questions.push(question);
            preview.push({
              ...question,
              correct_answer: parseInt(values[headers.indexOf('correct_answer')]) // Keep 1-based for display
            });
          }
        }

        // Validate questions
        const invalidQuestions = questions.filter(q => 
          !q.question.trim() || 
          q.options.some(opt => !opt.trim()) ||
          q.correct_answer < 0 || q.correct_answer > 3
        );

        if (invalidQuestions.length > 0) {
          setError('Some questions have invalid data. Please check your CSV format.');
          return;
        }

        setCsvData(questions);
        setPreviewData(preview);
        setError('');
      } catch (err) {
        setError('Error parsing CSV file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (csvData.length > 0) {
      onImport(csvData);
      onClose();
      setCsvData([]);
      setPreviewData([]);
      setError('');
    }
  };

  const handleClose = () => {
    onClose();
    setCsvData([]);
    setPreviewData([]);
    setError('');
  };

  const downloadTemplate = () => {
    const template = `question,option1,option2,option3,option4,correct_answer,explanation
"What is the capital of France?",Paris,London,Berlin,Madrid,1,Paris is the capital and largest city of France.
"What is 2 + 2?",3,4,5,6,2,2 + 2 equals 4.
"What is the largest planet in our solar system?",Earth,Mars,Jupiter,Saturn,3,Jupiter is the largest planet in our solar system.`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quiz_questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Quiz Questions from CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Upload a CSV file with quiz questions. The file should have the following columns:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
            <Typography variant="body2" component="code">
              question,option1,option2,option3,option4,correct_answer,explanation
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            • <strong>question:</strong> The question text<br/>
            • <strong>option1-4:</strong> The four answer options<br/>
            • <strong>correct_answer:</strong> The number of the correct option (1-4)<br/>
            • <strong>explanation:</strong> Optional explanation for the correct answer
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            sx={{ mb: 2, mr: 2 }}
          >
            Choose CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileUpload}
            />
          </Button>
          <Button
            variant="text"
            onClick={downloadTemplate}
            sx={{ mb: 2 }}
          >
            Download Template
          </Button>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {csvData.length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully loaded {csvData.length} questions from CSV file.
            </Alert>
          )}
        </Box>

        {previewData.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview (showing first {previewData.length} questions)
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Option 1</TableCell>
                    <TableCell>Option 2</TableCell>
                    <TableCell>Option 3</TableCell>
                    <TableCell>Option 4</TableCell>
                    <TableCell>Correct</TableCell>
                    <TableCell>Explanation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>
                          {row.question}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.options[0]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.options[1]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.options[2]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.options[3]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckIcon color="success" fontSize="small" />
                          <Typography variant="body2" sx={{ ml: 0.5 }}>
                            {row.correct_answer}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.explanation || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleImport} 
          variant="contained"
          disabled={csvData.length === 0}
        >
          Import {csvData.length} Questions
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportDialog; 